import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiEye, FiMapPin, FiCamera, FiSun } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Experiences = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const experiences = [
    {
      icon: FiEye,
      title: "Birdwatching & Wildlife",
      description: "Spot the endemic Sri Lanka Blue Magpie and Red-faced Malkoha in their natural habitat. Our private trails offer silent encounters with nature.",
      features: ["Private guide included", "High-end optics available", "Dawn & dusk sessions"]
    },
    {
      icon: FiMapPin,
      title: "Heritage & Culture",
      description: "Explore the magnificent Maduwanwela Walawwa, once home to the 'Black Prince' of Sabaragamuwa, with exclusive access and storytelling.",
      features: ["Private heritage tours", "Traditional lunch experience", "Historical artifacts viewing"]
    },
    {
      icon: FiCamera,
      title: "Geological Adventures",
      description: "Journey to the 500-million-year-old Waulpane Limestone Caves, featuring the only underground waterfall in Sri Lanka.",
      features: ["4WD cave access", "Fossil exploration", "Bat colony observation"]
    },
    {
      icon: FiSun,
      title: "Waterfall Circuits",
      description: "Discover hidden cascades known only to locals. Our exclusive waterfall map guides you to pristine natural pools.",
      features: ["Secret waterfall locations", "Swimming opportunities", "Photography workshops"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <section id="experiences" className="py-20 bg-forest-green relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'linear'
          }}
          className="w-full h-full opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 2px, transparent 2px),
                             radial-gradient(circle at 80% 20%, white 1px, transparent 1px),
                             radial-gradient(circle at 40% 80%, white 1px, transparent 1px)`,
            backgroundSize: '100px 100px, 150px 150px, 200px 200px'
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={itemVariants}
            className="text-4xl md:text-5xl font-playfair font-bold text-white mb-6"
          >
            Immersive Experiences
          </motion.h2>
          
          <motion.div 
            variants={itemVariants}
            className="w-24 h-1 bg-terracotta-earth mx-auto mb-8"
          ></motion.div>
          
          <motion.p 
            variants={itemVariants}
            className="text-xl font-lato text-white/90 max-w-3xl mx-auto leading-relaxed"
          >
            Beyond accommodation lies adventure. Each experience is crafted to connect you 
            with the profound beauty and rich heritage of the Kolonna region.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {experiences.map((experience, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="glass-card p-8 text-center group cursor-pointer"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta-earth/20 mb-6 group-hover:bg-terracotta-earth/30 transition-colors duration-300"
              >
                <SafeIcon 
                  icon={experience.icon} 
                  className="w-8 h-8 text-terracotta-earth" 
                />
              </motion.div>

              <h3 className="text-xl font-playfair font-semibold text-white mb-4">
                {experience.title}
              </h3>

              <p className="text-white/80 font-lato mb-6 leading-relaxed">
                {experience.description}
              </p>

              <ul className="space-y-2">
                {experience.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="text-sm font-lato text-white/70 flex items-center justify-center">
                    <span className="w-1 h-1 bg-terracotta-earth rounded-full mr-2"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="glass-button-primary px-8 py-4 text-white font-lato font-medium text-lg"
          >
            Plan Your Adventure
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Experiences;